/**
 * ConflictWarning Component
 * 
 * Displays scheduling conflicts and provides smart suggestions for alternative times.
 * Follows Material-UI design patterns and accessibility guidelines.
 */

import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    Typography,
    Paper,
    Stack,
    Tooltip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Warning as WarningIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Schedule as ScheduleIcon,
    ContentCopy as ContentCopyIcon,
    TrendingUp as TrendingUpIcon,
    AccessTime as AccessTimeIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { formatScheduledTime, CONFLICT_WINDOWS } from '../../utils/schedulingHelpers';
import { useTracking } from '../../hooks/useTracking';
import { EnhancedPlatformEvents } from '../../services/analytics/events';

// Styled components
const ConflictCard = styled(Paper)(({ theme, severity }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: `1px solid ${
        severity === 'high' ? theme.palette.error.main :
        severity === 'medium' ? theme.palette.warning.main :
        theme.palette.info.main
    }`,
    backgroundColor: 
        severity === 'high' ? theme.palette.error.light + '10' :
        severity === 'medium' ? theme.palette.warning.light + '10' :
        theme.palette.info.light + '10'
}));

const SuggestionChip = styled(Chip)(({ theme }) => ({
    cursor: 'pointer',
    margin: theme.spacing(0.5),
    '&:hover': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText
    }
}));

const ConflictWarning = ({
    conflicts = [],
    duplicateConflicts = [],
    suggestions = [],
    conflictWindow = CONFLICT_WINDOWS.DEFAULT,
    onSelectSuggestion,
    onChangeConflictWindow,
    onRefreshSuggestions,
    isLoading = false
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { trackEvent } = useTracking();
    const [expanded, setExpanded] = useState(true);
    const [showAllConflicts, setShowAllConflicts] = useState(false);

    // Calculate severity
    const hasHighSeverity = conflicts.some(c => c.timeDifference <= 3);
    const severity = hasHighSeverity ? 'error' : 
                    conflicts.length > 2 ? 'warning' : 'info';

    // Handle suggestion selection
    const handleSelectSuggestion = (suggestion) => {
        trackEvent(EnhancedPlatformEvents.CONFLICT_SUGGESTION_SELECTED.name, {
            suggestionType: suggestion.type,
            suggestionReason: suggestion.reason
        });
        onSelectSuggestion(suggestion);
    };

    // Get suggestion icon based on type
    const getSuggestionIcon = (type) => {
        switch (type) {
            case 'next_available':
                return <AccessTimeIcon fontSize="small" />;
            case 'peak_hours':
                return <TrendingUpIcon fontSize="small" />;
            case 'historical':
                return <ScheduleIcon fontSize="small" />;
            default:
                return <ScheduleIcon fontSize="small" />;
        }
    };

    // Format conflict message
    const getConflictSummary = () => {
        const timeConflictCount = conflicts.length;
        const duplicateCount = duplicateConflicts.length;
        
        if (timeConflictCount > 0 && duplicateCount > 0) {
            return `${timeConflictCount} timing conflict${timeConflictCount > 1 ? 's' : ''} and ${duplicateCount} duplicate content warning${duplicateCount > 1 ? 's' : ''}`;
        } else if (timeConflictCount > 0) {
            return `${timeConflictCount} post${timeConflictCount > 1 ? 's' : ''} scheduled within ${conflictWindow} minutes`;
        } else {
            return `${duplicateCount} duplicate content warning${duplicateCount > 1 ? 's' : ''}`;
        }
    };

    if (conflicts.length === 0 && duplicateConflicts.length === 0) {
        return null;
    }

    return (
        <Box sx={{ mt: 2, mb: 2 }}>
            <Alert 
                severity={severity}
                icon={<WarningIcon />}
                action={
                    <IconButton
                        aria-label={expanded ? "collapse" : "expand"}
                        onClick={() => setExpanded(!expanded)}
                        size="small"
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                }
            >
                <Typography variant="body2" fontWeight="medium">
                    Scheduling Conflict Detected
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {getConflictSummary()}
                </Typography>
            </Alert>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 2 }}>
                    {/* Timing Conflicts */}
                    {conflicts.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                                Timing Conflicts:
                            </Typography>
                            {conflicts.slice(0, showAllConflicts ? undefined : 2).map((conflict) => (
                                <ConflictCard 
                                    key={conflict.id} 
                                    elevation={0}
                                    severity={conflict.timeDifference <= 3 ? 'high' : 
                                             conflict.timeDifference <= 5 ? 'medium' : 'low'}
                                >
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        <WarningIcon 
                                            color={conflict.timeDifference <= 3 ? 'error' : 'warning'}
                                            fontSize="small"
                                        />
                                        <Box flex={1}>
                                            <Typography variant="body2" noWrap>
                                                {conflict.post_text?.substring(0, 60)}...
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {conflict.platforms?.join(', ')} • {formatScheduledTime(conflict.scheduled_post_date)}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </ConflictCard>
                            ))}
                            
                            {conflicts.length > 2 && !showAllConflicts && (
                                <Button 
                                    size="small" 
                                    onClick={() => setShowAllConflicts(true)}
                                    sx={{ mt: 1 }}
                                >
                                    Show {conflicts.length - 2} more conflicts
                                </Button>
                            )}
                        </Box>
                    )}

                    {/* Duplicate Content Warnings */}
                    {duplicateConflicts.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                                Duplicate Content:
                            </Typography>
                            <Alert severity="info" icon={<ContentCopyIcon />}>
                                <Typography variant="caption">
                                    Similar content posted within 24 hours may reduce engagement
                                </Typography>
                            </Alert>
                        </Box>
                    )}

                    {/* Suggested Times */}
                    {suggestions.length > 0 && (
                        <Box>
                            <Stack 
                                direction="row" 
                                alignItems="center" 
                                justifyContent="space-between"
                                sx={{ mb: 1 }}
                            >
                                <Typography variant="subtitle2">
                                    Suggested Times:
                                </Typography>
                                <Tooltip title="Refresh suggestions">
                                    <IconButton 
                                        size="small" 
                                        onClick={onRefreshSuggestions}
                                        disabled={isLoading}
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            
                            <Stack 
                                direction={isMobile ? "column" : "row"} 
                                spacing={1}
                                flexWrap="wrap"
                            >
                                {suggestions.map((suggestion, index) => (
                                    <SuggestionChip
                                        key={index}
                                        icon={getSuggestionIcon(suggestion.type)}
                                        label={
                                            <Box>
                                                <Typography variant="body2">
                                                    {suggestion.displayTime || formatScheduledTime(suggestion.time)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {suggestion.reason}
                                                </Typography>
                                            </Box>
                                        }
                                        onClick={() => handleSelectSuggestion(suggestion)}
                                        variant="outlined"
                                        color={suggestion.confidence > 0.8 ? "primary" : "default"}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Settings Information */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Alert severity="info" sx={{ py: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                You can adjust your conflict detection sensitivity in your{' '}
                                <Typography 
                                    component="span" 
                                    variant="caption" 
                                    color="primary" 
                                    sx={{ fontWeight: 'medium', cursor: 'pointer' }}
                                    onClick={() => window.location.href = '/profile'}
                                >
                                    Profile Settings
                                </Typography>
                                {' '}(currently set to {conflictWindow} minutes)
                            </Typography>
                        </Alert>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default ConflictWarning; 